import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  CurrencyDollarIcon,
  UsersIcon,
  DocumentTextIcon,
  ClockIcon,
  TrophyIcon,
  BanknotesIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBalance, setEditingBalance] = useState(false);
  const [balances, setBalances] = useState({ cash_balance: 0, bank_balance: 0 });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get('http://localhost/saininewsite/backend/dashboard.php');
      setStats(response.data.stats);
      setRecentTransactions(response.data.recent_transactions);
      setRecentInvoices(response.data.recent_invoices);
      setBalances({
        cash_balance: response.data.stats.cash_balance,
        bank_balance: response.data.stats.bank_balance
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateBalances = async () => {
    try {
      await axios.post('http://localhost/saininewsite/backend/update_balance.php', balances);
      setEditingBalance(false);
      fetchDashboardData();
    } catch (error) {
      console.error('Error updating balances:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  const statCards = [
    {
      title: 'Total Deposit',
      value: `₹${parseFloat(stats?.total_deposit || 0).toLocaleString()}`,
      icon: CurrencyDollarIcon,
      color: 'bg-green-500',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-600 dark:text-green-400',
    },
    {
      title: 'Total Withdraw',
      value: `₹${parseFloat(stats?.total_withdraw || 0).toLocaleString()}`,
      icon: BanknotesIcon,
      color: 'bg-red-500',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-600 dark:text-red-400',
    },
    {
      title: 'Customers Count',
      value: stats?.customers_count || 0,
      icon: UsersIcon,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-600 dark:text-blue-400',
    },
    {
      title: 'Invoice Count',
      value: stats?.invoice_count || 0,
      icon: DocumentTextIcon,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      textColor: 'text-purple-600 dark:text-purple-400',
    },
    {
      title: 'Pending Amount',
      value: `₹${parseFloat(stats?.pending_amount || 0).toLocaleString()}`,
      icon: ClockIcon,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-600 dark:text-yellow-400',
    },
    {
      title: 'Commission Earned',
      value: `₹${parseFloat(stats?.commission_earned || 0).toLocaleString()}`,
      icon: TrophyIcon,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
      textColor: 'text-indigo-600 dark:text-indigo-400',
    },
  ];

  const quickLinks = [
    { name: 'Add Customer', href: '/customers/add', icon: PlusIcon, color: 'bg-green-500' },
    { name: 'Add Transaction', href: '/transactions/add', icon: PlusIcon, color: 'bg-blue-500' },
    { name: 'Create Invoice', href: '/invoices/create', icon: PlusIcon, color: 'bg-purple-500' },
    { name: 'View Customers', href: '/customers/view', icon: EyeIcon, color: 'bg-gray-500' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`card p-6 ${card.bgColor}`}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-full ${card.color}`}>
                <card.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {card.title}
                </p>
                <p className={`text-2xl font-bold ${card.textColor}`}>
                  {card.value}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Cash & Bank Balance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card p-6"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Cash & Bank Balance
          </h2>
          <button
            onClick={() => setEditingBalance(!editingBalance)}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            {editingBalance ? 'Cancel' : 'Edit'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cash Balance
            </label>
            {editingBalance ? (
              <input
                type="number"
                value={balances.cash_balance}
                onChange={(e) => setBalances(prev => ({ ...prev, cash_balance: e.target.value }))}
                className="input-field"
              />
            ) : (
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ₹{parseFloat(balances.cash_balance).toLocaleString()}
              </p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bank Balance
            </label>
            {editingBalance ? (
              <input
                type="number"
                value={balances.bank_balance}
                onChange={(e) => setBalances(prev => ({ ...prev, bank_balance: e.target.value }))}
                className="input-field"
              />
            ) : (
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                ₹{parseFloat(balances.bank_balance).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        
        {editingBalance && (
          <div className="mt-4">
            <button onClick={updateBalances} className="btn-primary">
              Update Balances
            </button>
          </div>
        )}
      </motion.div>

      {/* Quick Links */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="card p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Links
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {quickLinks.map((link, index) => (
            <Link
              key={link.name}
              to={link.href}
              className="flex flex-col items-center p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <div className={`p-3 rounded-full ${link.color} mb-2`}>
                <link.icon className="h-6 w-6 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 text-center">
                {link.name}
              </span>
            </Link>
          ))}
        </div>
      </motion.div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="card p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Transactions
            </h2>
            <Link to="/transactions/view" className="text-primary-600 hover:text-primary-700 text-sm">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {recentTransactions.length > 0 ? (
              recentTransactions.map((transaction) => (
                <div key={transaction.id} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {transaction.customer_name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {transaction.mobile}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`font-medium ${transaction.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.transaction_type === 'deposit' ? '+' : '-'}₹{parseFloat(transaction.amount).toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(transaction.transaction_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No recent transactions
              </p>
            )}
          </div>
        </motion.div>

        {/* Recent Invoices */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 }}
          className="card p-6"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Invoices
            </h2>
            <Link to="/invoices/history" className="text-primary-600 hover:text-primary-700 text-sm">
              View All
            </Link>
          </div>
          <div className="space-y-3">
            {recentInvoices.length > 0 ? (
              recentInvoices.map((invoice) => (
                <div key={invoice.id} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {invoice.invoice_number}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {invoice.customer_name || 'Unknown Customer'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900 dark:text-white">
                      ₹{parseFloat(invoice.total_amount).toLocaleString()}
                    </p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      invoice.status === 'paid' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                        : invoice.status === 'partial'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                    }`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                No recent invoices
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
